import { motion } from 'framer-motion';
import { Briefcase, CheckCircle, GraduationCap } from 'lucide-react';
import React from 'react';

import { INDUSTRY_OPTIONS } from './onboardingTypes';
import type { OnboardingStepProps } from './onboardingTypes';

const OnboardingStep2_CareerProfile: React.FC<OnboardingStepProps> = ({
  data,
  onDataChange,
  questionType
}) => {
  // Handle both industry and experience level based on questionType
  
  if (questionType === 'experience') {
    const experienceLevels = [
      { value: 'Junior', label: 'Junior (0-2 years)', description: 'Entry level or early career' },
      { value: 'Mid-Level', label: 'Mid-Level (3-5 years)', description: 'Some experience in your field' },
      { value: 'Senior', label: 'Senior (6+ years)', description: 'Experienced professional' },
      { value: 'Executive', label: 'Executive/Leadership', description: 'Management or leadership role' }
    ];

    return (
      <div className="space-y-6">
        <div className="text-center">
          <GraduationCap className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold vscode-text-primary mb-2">What's your experience level?</h2>
          <p className="vscode-text-tertiary">This helps us tailor the optimization approach</p>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {experienceLevels.map((level) => (
            <motion.button
              key={level.value}
              onClick={() => onDataChange({ experienceLevel: level.value as any })}
              className={`w-full p-3 rounded-lg border text-left transition-all duration-200 ${
                data.experienceLevel === level.value
                  ? 'border-purple-500/50 bg-purple-500/10 text-purple-400'
                  : 'vscode-border vscode-bg-tertiary/50 vscode-text-secondary hover:border-purple-400/30'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-sm">{level.label}</h4>
                  <p className="text-xs opacity-80">{level.description}</p>
                </div>
                {data.experienceLevel === level.value && (
                  <CheckCircle className="w-5 h-5 text-purple-400" />
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  // Default: Industry selection
  return (
    <div className="space-y-6">
      <div className="text-center">
        <Briefcase className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold vscode-text-primary mb-2">What's your industry?</h2>
        <p className="vscode-text-tertiary">This helps us provide more relevant optimization</p>
      </div>

      <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
        {INDUSTRY_OPTIONS.map((category) => (
          <div key={category.category}>
            <h3 className="vscode-text-primary font-medium mb-2 text-sm">{category.category}</h3>
            <div className="grid grid-cols-1 gap-2 mb-4">
              {category.options.map((industry) => (
                <motion.button
                  key={industry}
                  onClick={() => onDataChange({ industry })}
                  className={`p-3 rounded-lg border text-left transition-all duration-200 text-sm ${
                    data.industry === industry
                      ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
                      : 'vscode-border vscode-bg-tertiary/50 vscode-text-secondary hover:border-cyan-400/30'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{industry}</span>
                    {data.industry === industry && (
                      <CheckCircle className="w-4 h-4 text-cyan-400" />
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(OnboardingStep2_CareerProfile);
